import { DateRange, Goal, Habit, TaskType } from '@griply/types';
import { sortGoals } from '@griply/utils';
import { useGoals, useSubGoals } from 'features/goals';
import { useTasksByGoalId } from 'features/tasks';
import React, { useMemo } from 'react';
import { GoalInsightsCard } from 'shared/components/connected/goal-insights-card';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { getSortedTasks } from 'shared/utils/get-sorted-tasks';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

import { EmptyChart } from '../empty-chart';
import { HabitInsightsCard } from '../habit-insights-card';
import * as Styled from './progress-grid.style';

export type ProgressGridProps = {
  goal: Goal;
  dateRange: DateRange;
};

export const ProgressGrid: React.FC<ProgressGridProps> = ({
  goal,
  dateRange,
}) => {
  const today = useToday();

  const goals = useGoals();
  const user = useUser();

  const tasks = useTasksByGoalId(goal.id);
  const subGoals = useSubGoals(goal.id);

  const sortedSubGoals = useMemo(
    () =>
      sortGoals(subGoals, {
        allGoals: goals,
        sortMode: goal?.goalSortingMode,
        customOrder: goal?.goalSorting,
        invertStartDateSorting: true,
      }),
    [goal?.goalSorting, goal?.goalSortingMode, goals, subGoals],
  );

  const goalHabits = useMemo(
    () =>
      getSortedTasks(
        tasks.filter(({ type }) => type === TaskType.Habit),
        {
          today,
          weekStartsOn: getWeekStartsOn(user),
          mode: goal?.taskSortingMode ? [goal?.taskSortingMode] : undefined,
          order: [
            ...(goal?.sections?.map(({ tasks }) => tasks).flat(1) ?? []),
            ...(goal?.taskSorting ?? []),
          ],
        },
      ) as Habit[],
    [
      goal?.sections,
      goal?.taskSorting,
      goal?.taskSortingMode,
      tasks,
      today,
      user,
    ],
  );

  return (
    <Styled.ChartContainer
      $mainGoalOnly={!goalHabits.length && !subGoals.length}
    >
      <Styled.ChartItem>
        {!goal?.metric?.id ? (
          <EmptyChart />
        ) : (
          <GoalInsightsCard
            goal={goal}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            noTitle
            hasBigTitle
            canUpdateProgress
          />
        )}
      </Styled.ChartItem>
      {sortedSubGoals.map((subGoal) => (
        <Styled.ChartItem key={subGoal.id}>
          <GoalInsightsCard
            key={subGoal.id}
            goal={subGoal}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            hasBigTitle
            canUpdateProgress
          />
        </Styled.ChartItem>
      ))}
      {goalHabits.map((habit) => (
        <Styled.ChartItem key={habit.id}>
          <HabitInsightsCard
            key={habit.id}
            habit={habit}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
          />
        </Styled.ChartItem>
      ))}
    </Styled.ChartContainer>
  );
};
